import { useEffect } from "react";
import { NOINDEX, NOINDEX_MODE, SITE_URL, onProdHost } from "@/config";
import { getLang, t } from "@/lib/i18n";
import { fieldState, getCoords, getLinks, isFilled, showMedia, useSite } from "@/lib/content";
import { PAGES } from "@/lib/routes";

function upsert(selector, create, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

const meta = (key, name, content) =>
  upsert(`meta[${key}="${name}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute(key, name);
    return m;
  }, "content", content);

export function setRobots(noindex) {
  meta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
}

function setHreflang(page, enabled) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((l) => l.remove());
  if (!enabled || !PAGES[page]) return;
  [["tr", PAGES[page].tr], ["en", PAGES[page].en], ["x-default", PAGES[page].tr]].forEach(([lang, p]) => {
    const l = document.createElement("link");
    l.setAttribute("rel", "alternate");
    l.setAttribute("hreflang", lang);
    l.setAttribute("href", `${SITE_URL}${p}`);
    document.head.appendChild(l);
  });
}

// Only live (verified) data goes into structured data
function structuredData(page, content, lang) {
  const home = `${SITE_URL}${PAGES.home[lang]}`;
  const place = { "@type": "TouristAttraction", "@id": `${SITE_URL}/#place`, name: "Woodia Polonez", url: home,
    containedInPlace: { "@type": "Park", name: "Polonezköy Tabiat Parkı" } };
  const coords = getCoords(content, false);
  if (coords) place.geo = { "@type": "GeoCoordinates", latitude: coords.lat, longitude: coords.lng };
  const links = getLinks(content, false);
  if (links.directions) place.hasMap = links.directions.href;
  if (links.phone) place.telephone = links.phone.href.replace("tel:", "");
  if (links.instagram) place.sameAs = [links.instagram.href];
  const hero = content?.media?.hero;
  if (showMedia(hero, false)) place.image = `${SITE_URL}${hero.src}`;
  const addr = fieldState(content?.visit?.address, false);
  if (addr.usable && isFilled(addr.value)) {
    place["@type"] = ["TouristAttraction", "LocalBusiness"];
    place.address = { "@type": "PostalAddress", streetAddress: addr.value, addressLocality: "Beykoz", addressRegion: "İstanbul", addressCountry: "TR" };
  }
  const crumbs = [{ "@type": "ListItem", position: 1, name: t("nav.home"), item: home }];
  if (page !== "home" && PAGES[page]) crumbs.push({ "@type": "ListItem", position: 2, name: t(`nav.${page}`) === `nav.${page}` ? t(`seo.${page}.title`).split(" | ")[0] : t(`nav.${page}`), item: `${SITE_URL}${PAGES[page][lang]}` });
  return { "@context": "https://schema.org", "@graph": [place, { "@type": "BreadcrumbList", itemListElement: crumbs }] };
}

function setJsonLd(data) {
  let el = document.getElementById("ld-json");
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "ld-json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function useSeo(page) {
  const { site, content } = useSite() || {};
  const noindex = NOINDEX_MODE === "audit" ? false : NOINDEX || !onProdHost() || site?.noindex !== false;
  const enLive = !!site?.en_live;
  const lang = getLang();
  useEffect(() => {
    const title = t(`seo.${page}.title`);
    const description = t(`seo.${page}.description`);
    const url = `${SITE_URL}${PAGES[page]?.[lang] ?? PAGES.home[lang]}`;
    const image = `${SITE_URL}/og-image.jpg`;
    document.title = title;
    meta("name", "description", description);
    setRobots(noindex);
    upsert('link[rel="canonical"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    }, "href", url);
    setHreflang(page, enLive);
    if (page !== "notFound") setJsonLd(structuredData(page, content, lang));
    meta("property", "og:title", title);
    meta("property", "og:description", description);
    meta("property", "og:url", url);
    meta("property", "og:image", image);
    meta("property", "og:type", "website");
    meta("property", "og:locale", t("ogLocale"));
    meta("property", "og:site_name", "Woodia Polonez");
    meta("name", "twitter:card", "summary_large_image");
    meta("name", "twitter:title", title);
    meta("name", "twitter:description", description);
    meta("name", "twitter:image", image);
  }, [page, noindex, enLive, lang, content]);
}
